// @flow
import { useSnackbar } from 'notistack';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from 'react-toggled';
import { useTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';

import BoostPopup from '../../../components/Popups/BoostPopup';
import SaveTranscriptPopup from '../../../components/Popups/SaveTranscriptPopup';
import WebsocketReconnectingBanner from '../../../components/Network/WebsocketReconnectingBanner';

import ScribeTranscriptListContainer from '../../../modules/scribe/ScribeTranscriptListContainer';
import ScribeVolumeController from '../../../modules/scribe/ScribeVolumeController';

import type { JoiningStatus } from '../../../types/conversation';
import { Displayable } from '../../../types/displayable';
import { isConversationEnded, isDefaultRoomId } from '../../../utils/ws-v1';
import { getSnack } from '../../../utils/snackbar';

import useStyles from './ConversationView.styles';

interface Props {
  conversationContainerHovered: boolean;
  conversationOrigin?: string;
  conversationRequested?: boolean;
  conversationStarted: boolean;
  currentPlatform: string;
  displayable: Displayable;
  editedWords: { text: string, transcriptionId: string }[];
  fullScreen: boolean;
  handleBoostPopup: Function;
  handleSaveTranscriptPopup: Function;
  hideZoomBroadcastingWarning: Function;
  insertSpeakerNameMode: boolean;
  isInConversation: boolean;
  joiningStatus: JoiningStatus;
  lightView: boolean;
  loading: boolean;
  loggingOut: boolean;
  muted: boolean;
  scribe: Object;
  setJoiningStatus: (joiningStatus: JoiningStatus) => void;
  showBoostModal: boolean;
  showSaveTranscriptsModal: boolean;
  showZoomBroadcastingWarning: boolean;
  socketConnectionStatus: string;
  status: Object;
  toggleConversationEndModal: Function;
  updateAudioSourceMode: boolean;
  user: Object;
}

const ConversationView = ({
  conversationContainerHovered,
  conversationOrigin,
  conversationRequested,
  conversationStarted,
  currentPlatform,
  displayable,
  editedWords,
  fullScreen,
  handleBoostPopup,
  handleSaveTranscriptPopup,
  hideZoomBroadcastingWarning,
  insertSpeakerNameMode,
  isInConversation,
  joiningStatus,
  lightView,
  loading,
  loggingOut,
  muted,
  scribe,
  setJoiningStatus,
  showBoostModal,
  showSaveTranscriptsModal,
  showZoomBroadcastingWarning,
  socketConnectionStatus,
  status,
  toggleConversationEndModal,
  updateAudioSourceMode,
  user,
}: Props) => {
  const classes = useStyles({ conversationContainerHovered });
  const theme = useTheme();
  const { t } = useTranslation();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (joiningStatus === 'accepted') {
      getSnack({ enqueueSnackbar, closeSnackbar })(t('conversation.inviteCard.joinedAvaRoom'));
      setJoiningStatus('none');
    }
  }, [joiningStatus]);

  return (
    <div style={{ display: 'flex' }}>
      <WebsocketReconnectingBanner className={classes.reconnectBanner} />
      {updateAudioSourceMode && (
        <div
          style={{
            backgroundColor: 'darksalmon',
            width: '100%',
            height: 30,
            textAlign: 'center',
          }}
        >
          Update audio source mode
        </div>
      )}
      {insertSpeakerNameMode && (
        <div style={{ backgroundColor: 'darksalmon', width: '100%', height: 30, textAlign: 'center' }}>
          Insert Speaker Name mode
        </div>
      )}
      {scribe && status.scribe && status.scribe.status === 'leaving' && (
        <div
          style={{
            backgroundColor: 'darksalmon',
            width: '100%',
            height: 30,
            textAlign: 'center',
            padding: 5,
            position: 'relative',
            zIndex: 102,
          }}
        >
          <span>{t('scribeEndRequested')}</span>
        </div>
      )}
      {conversationStarted && <ScribeTranscriptListContainer />}
      {window.isElectron && displayable.audio && !muted && isInConversation && !scribe && (
        <ScribeVolumeController className={classes.volumeController} />
      )}
      {currentPlatform === 'web' && status.participants && !scribe && !fullScreen && !isDefaultRoomId(status.id) && (
        <>
          {showSaveTranscriptsModal && <SaveTranscriptPopup callback={handleSaveTranscriptPopup} />}
          {showBoostModal && <BoostPopup open={showBoostModal} onClose={handleBoostPopup} editedWords={editedWords} />}
          {showZoomBroadcastingWarning && (
            <Dialog
              className={classes.zoomBroadcastWarningDialog}
              open={showZoomBroadcastingWarning}
              keepMounted
              onClose={() => {}}
            >
              <DialogContent>
                <div className={classes.zoomBroadcastWarningIconWrap}>
                  <Typography className={classes.zoomBroadcastWarningIconText}>CC</Typography>
                </div>
                <Typography variant="h4" className={classes.zoomBroadcastWarningTitle} align="center" gutterBottom>
                  {t('conversation.zoomExportWarning.title')}
                </Typography>
                <Typography variant="body1" align="center" gutterBottom>
                  {t('conversation.zoomExportWarning.body1')}
                </Typography>
                <Typography variant="body1" align="center" gutterBottom>
                  {t('conversation.zoomExportWarning.body2')}
                </Typography>
              </DialogContent>
              <DialogActions className={classes.zoomBroadcastWarningDialogActions}>
                <Button className={classes.zoomBroadcastWarningButton} onClick={() => toggleConversationEndModal(true)}>
                  {t('conversation.zoomExportWarning.confirm')}
                </Button>
                <Button
                  className={classes.zoomBroadcastWarningCancel}
                  onClick={hideZoomBroadcastingWarning}
                  variant="text"
                >
                  {t('conversation.zoomExportWarning.cancel')}
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </>
      )}
      {user && status?.id && !scribe && !lightView && !isDefaultRoomId(status.id) && isConversationEnded(status, user) && (
        <div>
          <Toggle>{() => <SaveTranscriptPopup callback={handleSaveTranscriptPopup} />}</Toggle>
        </div>
      )}
    </div>
  );
};

export default ConversationView;
